import {Box, Grid} from '@mui/material';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {t} from 'i18next';
import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {
  ProjectFieldDataType,
  ProjectFieldForm,
  projectFieldSchema,
} from './AddEditProjectFieldModalUtils';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  useAddProjectField,
  useProjectFieldAllDropdownLists,
  useGetProjectFieldDetails,
} from '../../../hooks/Projects/useProjectField';
import './AddEditProjectFieldModal.scss';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../shared/types';
import ArrTextArea from '../../common/arr-text-area/ArrTextArea';
import ArrCheckbox from '../../common/arr-checkbox/ArrCheckbox';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {
  IAddProjectFieldRequest,
  IProjectFieldDetailsResponse,
} from '../../../api/types/project.type';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {REGX, ToastType} from '../../../shared/constants';
import {isEmpty, isNil, omitBy, orderBy} from 'lodash';
import type {Resolver} from 'react-hook-form';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';

export type AddEditProjectFieldModalRef = {
  open: (projectId?: string) => void;
  close: () => void;
};
interface AddEditProjectFieldModalProps {
  onSaveSuccess?: (projectFieldDetails?: IProjectFieldDetailsResponse) => void;
}

const AddEditProjectFieldModal = forwardRef<
  AddEditProjectFieldModalRef,
  AddEditProjectFieldModalProps
>(({onSaveSuccess}, ref) => {
  const [showAddAddEditProjectFieldModal, setShowAddAddEditProjectFieldModal] =
    useState(false);
  const [isShowSuccessToast, setIsShowSuccessToast] = useState(false);
  const [isShowErrorToast, setIsShowErrorToast] = useState(false);
  const [isShowWarningToast, setIsShowWarningToast] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [showReindexModal, setShowReindexModal] = useState(false);
  const [pendingIsSearchable, setPendingIsSearchable] = useState<
    boolean | null
  >(null);
  const [currentProjectFieldId, setCurrentProjectFieldId] = useState<string>();
  const [successMessage, setSuccessMessage] = useState('');

  const resolver = zodResolver(
    projectFieldSchema()
  ) as unknown as Resolver<ProjectFieldForm>;

  const {
    control,
    register,
    reset,
    setValue,
    formState: {errors},
  } = useForm<ProjectFieldForm>({
    resolver,
    mode: 'onChange',
    defaultValues: {
      fieldName: '',
      fieldLabel: '',
      fieldDesc: '',
      isDerived: false,
      isEditable: false,
      isMultiValue: false,
      isSearchable: false,
      isTallyable: false,
      isCustomField: false,
      canBeSearched: false,
    },
  });

  const [
    fieldName,
    fieldLabel,
    fieldDesc,
    isDerived,
    isEditable,
    isMultiValue,
    isSearchable,
    isTallyable,
    selectedFieldGroup,
    selectedFieldClassfication,
    selectedFieldType,
    selectedFieldLength,
    selectedFieldDerived,
  ] = useWatch({
    control,
    name: [
      'fieldName',
      'fieldLabel',
      'fieldDesc',
      'isDerived',
      'isEditable',
      'isMultiValue',
      'isSearchable',
      'isTallyable',
      'selectedFieldGroup',
      'selectedFieldClassfication',
      'selectedFieldType',
      'selectedFieldLength',
      'selectedFieldDerived',
    ],
  });

  const {data: allDropdownLists} = useProjectFieldAllDropdownLists();

  const getFieldGroups = useMemo(() => {
    return orderBy(
      Object.entries(allDropdownLists?.listProjectFieldGroups ?? {}).map(
        ([id, name]) => ({id, name})
      ),
      'name'
    );
  }, [allDropdownLists?.listProjectFieldGroups]);

  const getFieldClassfication = useMemo(() => {
    return Object.entries(allDropdownLists?.listFieldClassification ?? {}).map(
      ([id, name]) => ({id, name})
    );
  }, [allDropdownLists?.listFieldClassification]);

  const getFieldTypes = useMemo(() => {
    return Object.entries(allDropdownLists?.listDataTypeCode ?? {}).map(
      ([id, name]) => ({id, name})
    );
  }, [allDropdownLists?.listDataTypeCode]);

  const getFieldLength = useMemo(() => {
    return Object.entries(
      allDropdownLists?.listDataTypeColumnLengthCode ?? {}
    ).map(([id, name]) => ({id, name}));
  }, [allDropdownLists?.listDataTypeColumnLengthCode]);

  const getFieldDerived = useMemo(() => {
    return Object.entries(allDropdownLists?.listDerivedField ?? {}).map(
      ([id, name]) => ({id, name})
    );
  }, [allDropdownLists?.listDerivedField]);

  const isFieldLengthEnabled = useMemo(
    () =>
      selectedFieldType?.name === ProjectFieldDataType.INT ||
      selectedFieldType?.name === ProjectFieldDataType.NVARCHAR,
    [selectedFieldType?.name]
  );

  const isFieldDerivedEnabled = useMemo(() => isDerived === true, [isDerived]);

  const isEmptyValue = useCallback(
    (value?: string) => (value?.trim() as string)?.length > 0,
    []
  );

  const isSaveEnabled = useMemo(() => {
    const isFieldLengthRequired = isFieldLengthEnabled
      ? isEmptyValue(selectedFieldLength?.name as string)
      : true;
    const isFieldDerivedRequired = isFieldDerivedEnabled
      ? isEmptyValue(selectedFieldDerived?.name as string)
      : true;
    return !(
      isEmptyValue(fieldName) &&
      isEmptyValue(fieldLabel) &&
      isEmptyValue(selectedFieldClassfication?.name as string) &&
      isEmptyValue(selectedFieldType?.name as string) &&
      isFieldLengthRequired &&
      isFieldDerivedRequired
    );
  }, [
    fieldLabel,
    fieldName,
    isEmptyValue,
    isFieldDerivedEnabled,
    isFieldLengthEnabled,
    selectedFieldClassfication?.name,
    selectedFieldDerived?.name,
    selectedFieldLength?.name,
    selectedFieldType?.name,
  ]);

  const addProjectFieldMutatiton = useAddProjectField();
  const {mutate: AddProjectFieldRequest, reset: ResetAddProjectFieldPayload} =
    addProjectFieldMutatiton;

  const loading = useMemo(
    () => addProjectFieldMutatiton.isPending,
    [addProjectFieldMutatiton.isPending]
  );
  const errorMessage = useMemo(
    () => addProjectFieldMutatiton.error?.message,
    [addProjectFieldMutatiton.error?.message]
  );

  const onClose = useCallback(() => {
    setShowAddAddEditProjectFieldModal(false);
    setCurrentProjectFieldId(undefined);
    reset();
  }, [reset]);

  useEffect(() => {
    if (addProjectFieldMutatiton.isSuccess) {
      setIsShowSuccessToast(true);
      onClose();
      setSuccessMessage(
        projectFieldDetails
          ? t('manageProjectField.editProjectField.successMessage')
          : t('manageProjectField.addProjectField.successMessage')
      );
      onSaveSuccess?.(projectFieldDetails);
      addProjectFieldMutatiton.reset();
    }
  }, [addProjectFieldMutatiton.isSuccess, onClose, onSaveSuccess]);

  useEffect(() => {
    if (addProjectFieldMutatiton.isError) {
      setIsShowErrorToast(true);
    }
  }, [addProjectFieldMutatiton.isError]);

  const {
    data: projectFieldDetails,
    isLoading: isLoadingDetails,
    isError: isErrorDetails,
  } = useGetProjectFieldDetails(
    currentProjectFieldId ?? '',
    !!currentProjectFieldId && showAddAddEditProjectFieldModal
  );

  const canBeSearched = useMemo(() => {
    return !!projectFieldDetails?.canBeSearched;
  }, [projectFieldDetails?.canBeSearched]);

  const isCustomField = useMemo(() => {
    return !!projectFieldDetails?.isCustomField;
  }, [projectFieldDetails?.isCustomField]);

  const initialIsDerived = useMemo(() => {
    return !!projectFieldDetails?.isDerived;
  }, [projectFieldDetails?.isDerived]);

  const projectFieldId = useMemo(
    () => projectFieldDetails?.fieldID ?? projectFieldDetails?.fieldID,
    [projectFieldDetails]
  );

  const isEditMode = useMemo(
    () => projectFieldId !== null && projectFieldId !== undefined,
    [projectFieldId]
  );

  const disableIsDerived = useMemo(
    () => !isCustomField || loading || (isEditMode && isEditable),
    [isCustomField, loading, isEditMode, isEditable]
  );

  const disableIsEditable = useMemo(
    () => !isCustomField || loading || (isEditMode && isDerived),
    [isCustomField, loading, isEditMode, isDerived]
  );

  const fieldNameState = useMemo(() => {
    if (errors.fieldName) {
      return 'error';
    }
    if (isEditMode) {
      return 'inactive';
    }
    return 'enabled';
  }, [errors.fieldName, isEditMode]);

  useEffect(() => {
    const data = projectFieldDetails;
    if (!currentProjectFieldId) {
      return;
    }

    if (isErrorDetails) {
      console.error('Error fetching project field details via hook');
      return;
    }

    if (data) {
      const toSelectItem = (
        id?: string | number | null,
        lookup?: Record<string, string> | undefined
      ): ISelectItem | undefined => {
        if (id === null || id === undefined) return undefined;
        const sid = String(id);
        // eslint-disable-next-line security/detect-object-injection
        const name = lookup?.[sid] ?? sid;
        return {id: sid, name};
      };

      const mapped = {
        fieldName: data.fieldName ?? '',
        fieldLabel: data.fieldLabel ?? '',
        fieldDesc: data.fieldDesc ?? '',
        isDerived: !!data.isDerived,
        isEditable: !!data.isEditable,
        isMultiValue: !!data.isMultiValue,
        isSearchable: !!data.isSearchable,
        isTallyable: !!data.isSet,
        isCustomField: !!data.isCustomField,
        canBeSearched: !!data.canBeSearched,
        selectedFieldGroup: toSelectItem(
          data.fieldGroupID ?? undefined,
          allDropdownLists?.listProjectFieldGroups
        ),
        selectedFieldClassfication: toSelectItem(
          data.fieldClassificationCode,
          allDropdownLists?.listFieldClassification
        ),
        selectedFieldType: toSelectItem(
          data.dataTypeCode,
          allDropdownLists?.listDataTypeCode
        ),
        selectedFieldLength: toSelectItem(
          data.dataTypeLengthCode ?? undefined,
          allDropdownLists?.listDataTypeColumnLengthCode
        ),
        selectedFieldDerived: toSelectItem(
          data.derivedValueSPName,
          allDropdownLists?.listDerivedField
        ),
      };

      reset(mapped);
    }
  }, [
    isLoadingDetails,
    isErrorDetails,
    currentProjectFieldId,
    allDropdownLists,
    projectFieldDetails,
    reset,
  ]);

  const onOpen = useCallback(
    (projectId?: string) => {
      setShowAddAddEditProjectFieldModal(true);

      if (!projectId) {
        setCurrentProjectFieldId(undefined);
        reset({
          fieldName: '',
          fieldLabel: '',
          fieldDesc: '',
          isDerived: false,
          isEditable: false,
          isMultiValue: false,
          isSearchable: false,
          isTallyable: false,
          selectedFieldGroup: undefined,
          selectedFieldClassfication: undefined,
          selectedFieldType: undefined,
          selectedFieldLength: undefined,
          selectedFieldDerived: undefined,
          isCustomField: false,
          canBeSearched: false,
        });

        return;
      }

      setCurrentProjectFieldId(projectId);
    },
    [reset]
  );

  useImperativeHandle(ref, () => ({
    open: onOpen,
    close: onClose,
  }));

  const onSuccessCloseToast = useCallback(() => {
    setIsShowSuccessToast(false);
  }, []);

  const onErrorCloseToast = useCallback(() => {
    setIsShowErrorToast(false);
  }, []);

  const handleFieldGroup = useCallback(
    (data: ISelectItem | null) => {
      if (isEditMode) return; // prevent changes in edit flow
      if (!isEmpty(data)) setValue('selectedFieldGroup', data);
    },
    [setValue, isEditMode]
  );

  const handleFieldClassfication = useCallback(
    (data: ISelectItem | null) => {
      if (!isEmpty(data)) setValue('selectedFieldClassfication', data);
    },
    [setValue]
  );

  const handleFieldType = useCallback(
    (data: ISelectItem | null) => {
      if (isEditMode) return;
      if (!isEmpty(data)) setValue('selectedFieldType', data);
    },
    [setValue, isEditMode]
  );

  const handleFieldLength = useCallback(
    (data: ISelectItem | null) => {
      if (isEditMode) return;
      if (!isEmpty(data)) setValue('selectedFieldLength', data);
    },
    [setValue, isEditMode]
  );

  const handleFieldDerived = useCallback(
    (data: ISelectItem | null) => {
      if (!isEmpty(data)) setValue('selectedFieldDerived', data);
    },
    [setValue]
  );

  const handleSave = useCallback(() => {
    const value = fieldName || '';
    if (!REGX.VALID_FIELD_NAME_FIRST_CHAR.test(value)) {
      setWarningMessage(
        t('manageProjectField.addProjectField.warningMessage.firstSpecialChar')
      );
      setIsShowWarningToast(true);
      return;
    }

    if (!REGX.VALID_FIELD_NAME_CHARS.test(value)) {
      setWarningMessage(
        t(
          'manageProjectField.addProjectField.warningMessage.containSpecialChar'
        )
      );
      setIsShowWarningToast(true);
      return;
    }
    const payload: IAddProjectFieldRequest = {
      fieldName: fieldName,
      fieldLabel: fieldLabel ?? '',
      fieldDesc: fieldDesc,
      fieldGroupID: Number.parseInt((selectedFieldGroup?.id as string) ?? '0'),
      fieldClassificationCode: (selectedFieldClassfication?.id as string) ?? '',
      dataTypeCode: (selectedFieldType?.id as string) ?? '',
      dataTypeLengthCode: (selectedFieldLength?.id as string) ?? '',
      derivedValueSPName: (selectedFieldDerived?.id as string) ?? '',
      isDerived: isDerived,
      isSet: isTallyable,
      isEditable: isEditable,
      isMultiValue: isMultiValue,
      isSearchable: isSearchable,
      fieldID: isEditMode ? Number(projectFieldId) : 0,
      isActive: true,
      isAddMode: true,
    };
    const params = omitBy(payload, (v) => isNil(v) || v === '');
    ResetAddProjectFieldPayload();
    AddProjectFieldRequest(params as any);
  }, [
    AddProjectFieldRequest,
    ResetAddProjectFieldPayload,
    fieldDesc,
    fieldLabel,
    fieldName,
    isDerived,
    isEditable,
    isMultiValue,
    isSearchable,
    isTallyable,
    selectedFieldClassfication?.id,
    selectedFieldDerived?.id,
    selectedFieldGroup?.id,
    selectedFieldLength?.id,
    selectedFieldType?.id,
  ]);

  const Divider = useCallback(
    () => <Box className={'project-field-divider'} />,
    []
  );

  const RenderToolTip = useCallback(() => {
    const titleKey = t(
      isEditMode
        ? 'manageProjectField.editProjectField.toolTip.title'
        : 'manageProjectField.addProjectField.toolTip.title'
    );

    const contentKey = t(
      isEditMode
        ? 'manageProjectField.editProjectField.toolTip.content'
        : 'manageProjectField.addProjectField.toolTip.content'
    );

    return (
      <ArrTooltipWrapper
        title={t(titleKey)}
        data-testid="tooltip-title"
        trigger={
          <InfoIcon
            tabIndex={0}
            aria-label={t('manageProjectField.userInforTooltipAriaLabel')}
            fontSize="medium"
            className="user-info-icon"
            data-testid="user-info-icon"
          />
        }
      >
        {t(contentKey)}
      </ArrTooltipWrapper>
    );
  }, [isEditMode, t]);

  return (
    <div data-testid={'add-project-field-modal'}>
      <ArrModal
        showModal={showAddAddEditProjectFieldModal}
        showClose={false}
        disabledClose={loading}
        onClose={onClose}
        title={t(
          isEditMode
            ? 'manageProjectField.editProjectField.toolTip.title'
            : 'manageProjectField.addProjectField.toolTip.title'
        )}
        titleEndIcon={isEditMode ? undefined : <RenderToolTip />}
        className="project-field-modal"
        dataTestId="add-edit-project-field-modal"
        buttons={[
          {
            id: 'add-project-field-cancel',
            label: t('common.cancel'),
            onClick: onClose,
            variant: 'secondary',
            size: 'medium',
            disabled: loading,
            dataTestId: 'aepf-cancel-btn',
          },
          {
            id: 'add-project-field-save',
            label: t('common.save'),
            onClick: handleSave,
            variant: 'primary',
            size: 'medium',
            loading: loading,
            disabled: loading || isSaveEnabled,
            ariaDisabled: loading || isSaveEnabled ? 'true' : 'false',
            dataTestId: 'aepf-save-btn',
          },
        ]}
      >
        {isLoadingDetails ? (
          <Box
            className="spin_loader_content"
            data-testid="spin_loader_content"
          >
            <ArrLoadingSpinner data-testid="spin_loader_content-spinner" />
          </Box>
        ) : (
          <Box className="project-field-input-content">
            <Grid container spacing={6}>
              <Grid size={6}>
                <ArrTextField
                  id="fieldName"
                  dataTestId="edit-project-template-name"
                  state={fieldNameState}
                  label={t('manageProjectField.addProjectField.fieldName')}
                  placeholder={t(
                    'manageProjectField.addProjectField.fieldName'
                  )}
                  {...register('fieldName')}
                  supportText={errors.fieldName?.message || ''}
                  disabled={isEditMode}
                  inputProps={{
                    'aria-label': t('manageProjectField.fieldNameAriaLabel'),
                  }}
                />
                <Divider />
                <div
                  className={isEditMode ? 'disabled-dropdown' : ''}
                  data-testid={`add-edit-project-field-modal-div`}
                >
                  <ArrSingleSelectDropdown
                    dropdownLabel={t(
                      'manageProjectField.addProjectField.fieldGroupName'
                    )}
                    dataTestId={'asd-project-field-group-dropdown'}
                    sizeVariant="large"
                    dropdownItems={getFieldGroups}
                    selectedValues={selectedFieldGroup as ISelectItem}
                    onSelectionChange={handleFieldGroup}
                    disabled={isEditMode}
                    searchable
                    showNoResultsText
                    inputLabelProps={{
                      'aria-label': t(
                        'manageProjectField.fieldGroupNameAriaLabel'
                      ),
                    }}
                    tabIndex={-1}
                  />
                </div>
                <Divider />
                <ArrTextArea
                  rows={4}
                  placeholder={
                    !isEditMode
                      ? t('manageProjectField.addProjectField.fieldDescription')
                      : ''
                  }
                  label={t(
                    'manageProjectField.addProjectField.fieldDescription'
                  )}
                  value={fieldDesc}
                  onChange={(value) => setValue('fieldDesc', value)}
                  dataTestId="field-description"
                />
                <Divider />
                {/* Checkboxes moved out of this left column so focus order
          is: input fields first, then checkboxes (rendered below). */}
              </Grid>
              <Grid size={6}>
                <ArrTextField
                  id="fieldLabel"
                  dataTestId="edit-project-name"
                  state={errors.fieldLabel ? 'error' : 'enabled'}
                  label={t('manageProjectField.addProjectField.fieldLabel')}
                  placeholder={t(
                    'manageProjectField.addProjectField.fieldLabel'
                  )}
                  {...register('fieldLabel')}
                  supportText={errors.fieldLabel?.message || ''}
                  inputProps={{
                    'aria-label': t('manageProjectField.fieldLabelAriaLabel'),
                  }}
                />
                <Divider />
                <ArrSingleSelectDropdown
                  dropdownLabel={t(
                    'manageProjectField.addProjectField.fieldClassification'
                  )}
                  dataTestId={'asd-project-field-classification-dropdown'}
                  sizeVariant="large"
                  dropdownItems={getFieldClassfication}
                  selectedValues={selectedFieldClassfication as ISelectItem}
                  onSelectionChange={handleFieldClassfication}
                  searchable
                  showNoResultsText
                  inputLabelProps={{
                    'aria-label': t(
                      'manageProjectField.fieldClassificationAriaLabel'
                    ),
                  }}
                  tabIndex={-1}
                />
                <Divider />
                <div
                  className={isEditMode ? 'disabled-dropdown' : ''}
                  data-testid={`add-edit-project-field-modal-div-btn`}
                >
                  <ArrSingleSelectDropdown
                    dropdownLabel={t(
                      'manageProjectField.addProjectField.fieldType'
                    )}
                    dataTestId={'asd-project-field-type-dropdown'}
                    sizeVariant="large"
                    dropdownItems={getFieldTypes}
                    selectedValues={selectedFieldType as ISelectItem}
                    onSelectionChange={handleFieldType}
                    disabled={isEditMode}
                    searchable
                    showNoResultsText
                    inputLabelProps={{
                      'aria-label': t(
                        'manageProjectField.fieldTypeRequiredAriaLabel'
                      ),
                    }}
                    tabIndex={-1}
                  />
                </div>
                {isFieldLengthEnabled && (
                  <Fragment>
                    <Divider />
                    <div
                      className={isEditMode ? 'disabled-dropdown' : ''}
                      data-testid={`add-edit-project-field-modal-div-button`}
                    >
                      <ArrSingleSelectDropdown
                        dropdownLabel={t(
                          'manageProjectField.addProjectField.fieldLength'
                        )}
                        dataTestId={'asd-project-field-length-dropdown'}
                        sizeVariant="large"
                        dropdownItems={getFieldLength}
                        selectedValues={selectedFieldLength as ISelectItem}
                        onSelectionChange={handleFieldLength}
                        disabled={isEditMode}
                        inputLabelProps={{
                          'aria-label': t(
                            'manageProjectField.fieldLengthAriaLabel'
                          ),
                        }}
                        tabIndex={-1}
                      />
                    </div>
                  </Fragment>
                )}
                {isFieldDerivedEnabled && (
                  <Fragment>
                    <Divider />
                    <div
                      className={
                        isEditMode && initialIsDerived
                          ? 'disabled-dropdown'
                          : ''
                      }
                      data-testid={`add-edit-project-field-modal-div-icon`}
                    >
                      <ArrSingleSelectDropdown
                        dropdownLabel={t(
                          'manageProjectField.addProjectField.fieldDerived'
                        )}
                        dataTestId={'asd-project-field-derived-dropdown'}
                        sizeVariant="large"
                        dropdownItems={getFieldDerived}
                        selectedValues={selectedFieldDerived as ISelectItem}
                        onSelectionChange={handleFieldDerived}
                        disabled={isEditMode ? initialIsDerived : false}
                        inputLabelProps={{
                          'aria-label': t(
                            'manageProjectField.fieldDerivedAriaLabel'
                          ),
                        }}
                        tabIndex={-1}
                      />
                    </div>
                  </Fragment>
                )}
              </Grid>
            </Grid>{' '}
            <Grid container spacing={6}>
              <Grid size={3}>
                <Grid container direction="column" spacing={1}>
                  <Grid>
                    <ArrCheckbox
                      labelText={t(
                        'manageProjectField.addProjectField.isDerived'
                      )}
                      data-testid={'checkbox-is-derived'}
                      size="small"
                      checked={isDerived}
                      onChange={(value) => {
                        if (isEditMode) {
                          if (!isCustomField || initialIsDerived) return;
                          if (disableIsDerived) return;
                        }
                        setValue('isDerived', value);
                      }}
                      aria-label={`checkbox-is-derived-cb`}
                    />
                  </Grid>
                  <Grid>
                    <ArrCheckbox
                      labelText={t(
                        'manageProjectField.addProjectField.isTallyable'
                      )}
                      data-testid={'checkbox-is-tallyable'}
                      size="small"
                      checked={isTallyable}
                      onChange={(value) => setValue('isTallyable', value)}
                      aria-label={`checkbox-is-tallyablecb`}
                    />
                  </Grid>
                  <Grid>
                    <ArrCheckbox
                      labelText={t(
                        'manageProjectField.addProjectField.isMultiValue'
                      )}
                      data-testid={'checkbox-is-multivalue'}
                      size="small"
                      checked={isMultiValue}
                      onChange={(value) => {
                        if (isEditMode) return;
                        setValue('isMultiValue', value);
                      }}
                      aria-label={`checkbox-is-multi-cb`}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Right column: DOM order after left column */}
              <Grid size={3}>
                <Grid container direction="column" spacing={1}>
                  <Grid>
                    <ArrCheckbox
                      labelText={t(
                        'manageProjectField.addProjectField.isSearchable'
                      )}
                      data-testid={'checkbox-is-searchable'}
                      size="small"
                      checked={isSearchable}
                      onChange={(value) => {
                        if (isEditMode) {
                          if (!canBeSearched) return;
                          setPendingIsSearchable(value);
                          setShowReindexModal(true);
                          return;
                        }
                        setValue('isSearchable', value);
                      }}
                      aria-label={`checkbox-is-searchable-cb`}
                    />
                  </Grid>
                  <Grid>
                    <ArrCheckbox
                      labelText={t(
                        'manageProjectField.addProjectField.isEditable'
                      )}
                      data-testid={'checkbox-is-editable'}
                      size="small"
                      checked={isEditable}
                      aria-label={`checkbox-is-editable-cb`}
                      onChange={(value) => {
                        if (isEditMode) {
                          if (!isCustomField) return;
                          if (disableIsEditable) return;
                        }
                        setValue('isEditable', value);
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
      </ArrModal>
      <ArrModal
        showModal={showReindexModal}
        ariaLabel={t('manageProjectField.modal.titleReindex')}
        title={t('manageProjectField.modal.titleReindex')}
        ariaDescription={t('manageProjectField.modal.descriptionReindex')}
        description={t('manageProjectField.modal.descriptionReindex')}
        onClose={() => {
          setShowReindexModal(false);
          setPendingIsSearchable(null);
        }}
        dataTestId="manageProjectField-modal"
        buttons={[
          {
            id: 'ok-button',
            label: t('common.ok'),
            dataTestId: 'mpf-ok-button',
            onClick: () => {
              if (pendingIsSearchable !== null) {
                setValue('isSearchable', pendingIsSearchable);
              }
              setShowReindexModal(false);
              setPendingIsSearchable(null);
            },
            variant: 'primary',
          },
        ]}
      />
      {isShowSuccessToast && (
        <ArrToastNotification
          message={successMessage}
          status={[ToastType.Success]}
          onClose={onSuccessCloseToast}
          closeButtonAriaLabel={t(
            'manageProjectField.dismissNotificationAriaLable'
          )}
        />
      )}
      {isShowErrorToast && (
        <ArrToastNotification
          message={errorMessage || ''}
          status={[ToastType.Error]}
          onClose={onErrorCloseToast}
          closeButtonAriaLabel={t(
            'manageProjectField.dismissNotificationAriaLable'
          )}
        />
      )}
      {isShowWarningToast && (
        <ArrToastNotification
          message={warningMessage}
          status={[ToastType.Warning]}
          onClose={() => setIsShowWarningToast(false)}
          closeButtonAriaLabel={t(
            'manageProjectField.dismissNotificationAriaLable'
          )}
        />
      )}
    </div>
  );
});

export {AddEditProjectFieldModal};
